import { Column, ColumnKeys, Dependencies, SubTaskStatusCustomValues, SubTaskStatusDefaultValues, SubTasksWebComponentSchema } from '../types';
import { defaultPageSizeOptions, GridViewMetadata } from '../../../grid/gridView/types';
import { Filter } from '../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { SubTasksGridDataProvider } from '../dataProvider/SubTasksGridDataProvider';
import { html } from 'lit';
import { TaskStatus, getTaskStatusLabel, TaskType } from '../../networkTasks/types';
import { getLabels } from '../../networkTasks/helpers/gridConfigs';

export class GridConfig {
  private filtersUiConfig = [];
  private taskHasContainers;

  public getDataForView(dependencies: Dependencies): SubTasksWebComponentSchema {
    this.taskHasContainers = dependencies.task?.hasContainers;

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'network-subtasks',
      filters: this.getDefaultFilters(),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: (dependencies.task?.type === TaskType.TASK_TYPE_SCAN_PARENT || dependencies.task?.type === TaskType.TASK_TYPE_SCAN_EXCHANGE_PARENT),
      hasPagination: true,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.getColumnRenderers(dependencies),
      showGlobalActions: true,
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(dependencies),
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: new SubTasksGridDataProvider(dependencies),
      enableCSVExport: false,
      sortColumnName: ColumnKeys.COMPANIES,
      sortDirection: 'desc',
      hideHeaderActions: true,
      enableRowClickEvent: true,
    };

    return {
      viewMetaData,
    };
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    const defaultFilters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.TARGETS]: {
          contains: '',
        },
      },
      {
        [ColumnKeys.STATUS]: {
          in: [],
        },
      },
    ];

    if (this.taskHasContainers) {
      const filterToInsert = {
        [ColumnKeys.CONTAINERS]: {
          contains: '',
        },
      };
      const indexToInsert = 1;
      const deleteCount = 0;
      defaultFilters.splice(indexToInsert, deleteCount, filterToInsert);
    }

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    const keys = [ColumnKeys.TARGETS, ColumnKeys.STATUS];

    if (this.taskHasContainers) {
      const indexToInsert = 1;
      const deleteCount = 0;
      keys.splice(indexToInsert, deleteCount, ColumnKeys.CONTAINERS);
    }

    return keys;
  }

  private getColumns(): Column[] {
    return [Column.TARGETS, Column.CONTAINERS, Column.STATUS, Column.COMPANIES];
  }

  private getColumnRenderers(dependencies: Dependencies) {
    const { translateFn } = dependencies;
    return {
      [ColumnKeys.TARGETS]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const computerName = rowData.item?.targets[0]?.name;
        return html`<gz-grid-cell-default-renderer .label="${computerName}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.CONTAINERS]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const containers = rowData.item?.targets[0]?.displayEntities;
        const containerNames = containers?.map(({ name }) => name);
        const containerLabel = containerNames ? containerNames?.join(', ') : '';

        return html`<gz-grid-cell-default-renderer .label="${containerLabel}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.STATUS]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const status = getTaskStatusLabel(rowData.item?.status, translateFn) ? getTaskStatusLabel(rowData.item?.status, translateFn) : translateFn('Unknown');
        return html`<gz-grid-cell-default-renderer .label="${status}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.COMPANIES]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const company = rowData.item?.companies?.length === 1 ? rowData.item?.companies[0]?.name : rowData.item?.companies?.length + ' ' + translateFn('companies');
        return html`<gz-grid-cell-default-renderer .label="${company}"></gz-grid-cell-default-renderer>`;
      },
    };
  }

  private getColumnDetails(dependencies: Dependencies): Array<ColumnDetails<Column, ColumnKeys>> {
    const { permissions, translateFn } = dependencies;
    const columnDetails = [
      {
        column: Column.TARGETS,
        key: ColumnKeys.TARGETS,
        columnName: translateFn('Computer name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        values: getLabels(translateFn, this.getAvailableStatusForSubTasks(dependencies), getTaskStatusLabel),
        type: ColumnType.ENUM,
        removable: false,
        sortable: false,
      },
    ];

    if (permissions.canViewCompanyColumn) {
      const indexToInsert = 3;
      const deleteCount = 0;
      const columnToInsert = {
        column: Column.COMPANIES,
        key: ColumnKeys.COMPANIES,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: false,
        sortable: false,
        enumFilterCanSelectAll: false,
        canSelectAll: true,
        filteringDisabled: true,
      };

      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    if (this.taskHasContainers) {
      const indexToInsert = 1;
      const deleteCount = 0;
      const columnToInsert = {
        column: Column.CONTAINERS,
        key: ColumnKeys.CONTAINERS,
        columnName: translateFn('Container name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
      };

      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    return columnDetails;
  }

  private getAvailableStatusForSubTasks(dependencies: Dependencies): Array<TaskStatus> {
    const { task } = dependencies;
    const listOfSubTaskStatus: Array<TaskStatus> = [];

    if (task?.type && SubTaskStatusCustomValues[task?.type]) {
      listOfSubTaskStatus.push(...SubTaskStatusCustomValues[task?.type]);
    } else {
      listOfSubTaskStatus.push(...SubTaskStatusDefaultValues);
    }

    return listOfSubTaskStatus;
  }
}
